import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlaidLink } from 'react-plaid-link';
import { Send, Download, ChevronDown, Calendar, RefreshCw, Plus, Link, Unlink, CheckCircle, AlertCircle, Building2, CreditCard, Wallet, PiggyBank, TrendingUp, X, Pencil, Check, Trash2, Loader2, Receipt, FileText, DollarSign, ArrowUpRight, ArrowDownRight, Filter, Search, Upload, FileUp, Image, FileSpreadsheet, Save, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, PieChart, Pie, Cell, BarChart, Bar, YAxis, Tooltip } from 'recharts';
import { toast } from 'sonner';
import { useAuth, useUserId } from '../context/UserAuthContext';
import { useDemoMode } from '../context/DemoContext';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

// Real Plaid Link Button Component
const PlaidLinkButton = ({ onSuccess, buttonText = "Link Bank Account", className = "", userId }) => {
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch link token on mount
  useEffect(() => {
    const fetchLinkToken = async () => {
      if (!userId) return;
      
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/plaid/create_link_token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'Failed to create link token');
        }
        
        setLinkToken(data.link_token);
        setError(null);
      } catch (err) {
        console.error('Error fetching link token:', err);
        setError(err.message);
        toast.error(`Failed to initialize Plaid: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchLinkToken();
  }, [userId]);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (publicToken, metadata) => {
      console.log('Plaid Link success:', metadata);
      setLoading(true);
      
      try {
        // Exchange public token for access token
        const exchangeResponse = await fetch(`${API_URL}/api/plaid/exchange_public_token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            public_token: publicToken,
            account_id: metadata.accounts[0]?.id || 'primary',
            user_id: userId
          })
        });
        
        const responseText = await exchangeResponse.text();
        let data;
        
        try {
          data = JSON.parse(responseText);
        } catch (e) {
          console.error('Failed to parse response:', responseText);
          throw new Error('Invalid server response');
        }
        
        if (!exchangeResponse.ok) {
          throw new Error(data.detail || 'Failed to exchange token');
        }
        
        toast.success(`Successfully connected ${metadata.institution.name}!`);
        
        // Call the parent callback with metadata
        onSuccess(publicToken, {
          ...metadata,
          isDemo: false,
          exchangeSuccess: data.success
        });
      } catch (err) {
        console.error('Error exchanging token:', err);
        toast.error(`Failed to connect account: ${err.message}`);
      } finally {
        setLoading(false);
      }
    },
    onExit: (err) => {
      if (err) {
        console.error('Plaid Link error:', err);
        toast.error('Connection cancelled or failed');
      }
    },
    onEvent: (eventName, metadata) => {
      console.log('Plaid event:', eventName, metadata);
    }
  });

  return (
    <button
      onClick={() => open()}
      disabled={!ready || loading}
      className={`flex items-center gap-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white px-4 py-2 rounded-xl font-medium transition-colors ${className}`}
      data-testid="plaid-link-button"
    >
      {loading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
      {loading ? 'Connecting...' : !ready ? 'Loading...' : buttonText}
    </button>
  );
};

// Bank/Financial Institution Connections Component
const ConnectionsPanel = ({ connections, setConnections, onAccountsUpdate, userId }) => {
  const [syncingItemId, setSyncingItemId] = useState(null);

  const handlePlaidSuccess = async (publicToken, metadata) => {
    // Use the real account data from Plaid
    const accounts = metadata.accounts || [];
    const totalBalance = accounts.reduce((sum, acc) => sum + (acc.balances?.current || 0), 0);
    
    // Add to connections
    const newConnection = {
      id: Date.now().toString(),
      name: metadata.institution?.name || 'Bank Account',
      icon: '🏦',
      type: 'bank',
      status: 'connected',
      lastSync: new Date().toLocaleString(),
      accounts: accounts,
      totalBalance,
      institutionId: metadata.institution?.institution_id,
      isDemo: metadata.isDemo || false
    };

    const updatedConnections = [...connections, newConnection];
    setConnections(updatedConnections);
    localStorage.setItem('financeConnections', JSON.stringify(updatedConnections));
    
    toast.success(`${metadata.institution?.name || 'Bank'} connected successfully!`);
    
    if (onAccountsUpdate) {
      onAccountsUpdate(accounts);
    }
  };

  const syncAccounts = async (connectionId) => {
    setSyncingItemId(connectionId);
    
    try {
      // Fetch real account balances from Plaid
      const response = await fetch(`${API_URL}/api/plaid/accounts?user_id=${userId}`);
      
      if (response.ok) {
        const data = await response.json();
        const accounts = data.accounts || [];
        
        setConnections(prev => prev.map(conn => {
          if (conn.id === connectionId) {
            const totalBalance = accounts.reduce((sum, acc) => sum + (acc.balances?.current || 0), 0);
            return {
              ...conn,
              accounts: accounts,
              totalBalance,
              lastSync: new Date().toLocaleString()
            };
          }
          return conn;
        }));
        
        toast.success('Accounts synced successfully');
      } else {
        throw new Error('Failed to sync accounts');
      }
    } catch (err) {
      console.error('Sync error:', err);
      toast.error('Failed to sync accounts');
    } finally {
      setSyncingItemId(null);
    }
  };

  const disconnectAccount = (accountId) => {
    const updatedConnections = connections.filter(c => c.id !== accountId);
    setConnections(updatedConnections);
    localStorage.setItem('financeConnections', JSON.stringify(updatedConnections));
    toast.success('Account disconnected');
  };

  return (
    <div className="bg-black border border-gray-700 rounded-2xl p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Link size={18} className="text-cyan-400" />
            Connected Accounts
          </h3>
          <p className="text-gray-500 text-sm">Link your banks, cards, and investment accounts</p>
        </div>
        <PlaidLinkButton 
          onSuccess={handlePlaidSuccess}
          buttonText="+ Add Connection"
          userId={userId}
        />
      </div>

      {connections.length > 0 ? (
        <div className="space-y-3">
          {connections.map((conn) => (
            <div key={conn.id} className="flex items-center justify-between p-4 bg-gray-900/50 rounded-xl border border-gray-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-lg">
                  {conn.icon || '🏦'}
                </div>
                <div>
                  <div className="text-white font-medium">{conn.name}</div>
                  <div className="text-green-400 text-xs flex items-center gap-1">
                    <CheckCircle size={10} />
                    Connected
                  </div>
                  {conn.totalBalance !== undefined && (
                    <div className="text-cyan-400 text-sm font-semibold">
                      ${conn.totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </div>
                  )}
                </div>
                {conn.lastSync && (
                  <div className="text-gray-500 text-xs ml-4">
                    Last synced: {conn.lastSync}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => syncAccounts(conn.id)}
                  disabled={syncingItemId === conn.id}
                  className="p-2 text-gray-400 hover:text-cyan-400 hover:bg-gray-800 rounded-lg transition-colors"
                  title="Sync transactions"
                >
                  <RefreshCw size={16} className={syncingItemId === conn.id ? 'animate-spin' : ''} />
                </button>
                <button
                  onClick={() => disconnectAccount(conn.id)}
                  className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-800 rounded-lg transition-colors"
                  title="Disconnect"
                >
                  <Unlink size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-900/50 rounded-xl border border-dashed border-gray-700">
          <Wallet size={32} className="mx-auto text-gray-600 mb-2" />
          <p className="text-gray-400">No accounts connected</p>
          <p className="text-gray-500 text-sm mt-1">Connect your bank or card to sync transactions</p>
        </div>
      )}
    </div>
  );
};

// Document Upload Panel for parsing CSV/Images with AI
const DocumentUploadPanel = ({ userId, onTransactionsParsed }) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [parsedTransactions, setParsedTransactions] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleFileUpload = async (file) => {
    if (!userId) {
      toast.error('Please log in to upload documents');
      return;
    }

    // Validate file type
    const allowedTypes = ['.csv', '.png', '.jpg', '.jpeg', '.webp', '.pdf'];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!allowedTypes.includes(fileExt)) {
      toast.error(`Unsupported file type. Allowed: ${allowedTypes.join(', ')}`);
      return;
    }

    setUploading(true);
    setParsedTransactions([]);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_id', userId);

      const response = await fetch(`${API_URL}/api/transaction-upload/parse`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to parse document');
      }

      if (data.transactions && data.transactions.length > 0) {
        setParsedTransactions(data.transactions);
        setShowPreview(true);
        toast.success(`Found ${data.transactions.length} transactions in ${file.name}`);
      } else {
        toast.warning('No transactions found in the uploaded document');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(`Failed to parse document: ${err.message}`);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSaveTransactions = async () => {
    if (parsedTransactions.length === 0) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/transaction-upload/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          transactions: parsedTransactions
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save transactions');
      }

      toast.success(`Saved ${data.saved_count} transactions!`);
      setShowPreview(false);
      setParsedTransactions([]);
      
      // Notify parent to refresh
      if (onTransactionsParsed) {
        onTransactionsParsed(parsedTransactions);
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const updateTransaction = (index, field, value) => {
    setParsedTransactions(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeTransaction = (index) => {
    setParsedTransactions(prev => prev.filter((_, i) => i !== index));
  };

  const categoryOptions = [
    { id: 'food', label: 'Food & Dining', icon: '🍔' },
    { id: 'gas', label: 'Gas & Fuel', icon: '⛽' },
    { id: 'studio', label: 'Studio', icon: '🎬' },
    { id: 'rent', label: 'Rent', icon: '🏠' },
    { id: 'utilities', label: 'Utilities', icon: '💡' },
    { id: 'entertainment', label: 'Entertainment', icon: '🎭' },
    { id: 'shopping', label: 'Shopping', icon: '🛒' },
    { id: 'travel', label: 'Travel', icon: '✈️' },
    { id: 'income', label: 'Income', icon: '💰' },
    { id: 'subscription', label: 'Subscriptions', icon: '📱' },
    { id: 'health', label: 'Health', icon: '🏥' },
    { id: 'education', label: 'Education', icon: '📚' },
    { id: 'other', label: 'Other', icon: '📋' }
  ];

  return (
    <div className="bg-black border border-gray-700 rounded-2xl p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-white font-semibold flex items-center gap-2">
            <FileUp size={18} className="text-emerald-400" />
            Upload Transactions
          </h3>
          <p className="text-gray-500 text-sm">Upload CSV files or receipt images for AI parsing</p>
        </div>
      </div>

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
          isDragging 
            ? 'border-emerald-500 bg-emerald-500/10' 
            : 'border-gray-700 hover:border-gray-600'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-testid="upload-drop-zone"
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 size={48} className="text-emerald-400 animate-spin" />
            <p className="text-white font-medium">Parsing document with AI...</p>
            <p className="text-gray-500 text-sm">This may take a few moments</p>
          </div>
        ) : (
          <>
            <div className="flex justify-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <FileSpreadsheet size={24} className="text-emerald-400" />
              </div>
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Image size={24} className="text-purple-400" />
              </div>
            </div>
            <p className="text-white font-medium mb-2">
              Drag & drop a file here, or click to browse
            </p>
            <p className="text-gray-500 text-sm mb-4">
              Supports: CSV, PNG, JPG, JPEG, WEBP, PDF
            </p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2.5 rounded-xl font-medium transition-colors"
              data-testid="upload-browse-btn"
            >
              <Upload size={18} />
              Browse Files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.png,.jpg,.jpeg,.webp,.pdf"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="upload-file-input"
            />
          </>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && parsedTransactions.length > 0 && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-gray-900 rounded-2xl w-full max-w-4xl max-h-[90vh] border border-gray-700 shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-lg font-bold text-white">Review Parsed Transactions</h3>
                <p className="text-gray-400 text-sm">{parsedTransactions.length} transactions found</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSaveTransactions}
                  disabled={saving || parsedTransactions.length === 0}
                  className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-700 text-white px-4 py-2 rounded-xl font-medium transition-colors"
                  data-testid="save-transactions-btn"
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  {saving ? 'Saving...' : 'Save All'}
                </button>
                <button onClick={() => setShowPreview(false)} className="p-2 hover:bg-gray-800 rounded-lg">
                  <X size={18} className="text-gray-400" />
                </button>
              </div>
            </div>

            {/* Transactions List */}
            <div className="p-4 overflow-y-auto flex-1">
              <div className="space-y-3">
                {parsedTransactions.map((txn, idx) => (
                  <div key={txn.id || idx} className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                    <div className="flex items-start gap-4">
                      {/* Date */}
                      <div className="w-28 flex-shrink-0">
                        <label className="text-gray-500 text-xs block mb-1">Date</label>
                        <input
                          type="date"
                          value={txn.date}
                          onChange={(e) => updateTransaction(idx, 'date', e.target.value)}
                          className="w-full bg-black border border-gray-600 rounded-lg px-2 py-1.5 text-white text-sm"
                        />
                      </div>

                      {/* Description */}
                      <div className="flex-1">
                        <label className="text-gray-500 text-xs block mb-1">Description</label>
                        <input
                          type="text"
                          value={txn.description}
                          onChange={(e) => updateTransaction(idx, 'description', e.target.value)}
                          className="w-full bg-black border border-gray-600 rounded-lg px-2 py-1.5 text-white text-sm"
                        />
                      </div>

                      {/* Amount */}
                      <div className="w-28 flex-shrink-0">
                        <label className="text-gray-500 text-xs block mb-1">Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={txn.amount}
                          onChange={(e) => updateTransaction(idx, 'amount', parseFloat(e.target.value) || 0)}
                          className="w-full bg-black border border-gray-600 rounded-lg px-2 py-1.5 text-white text-sm"
                        />
                      </div>

                      {/* Category */}
                      <div className="w-40 flex-shrink-0">
                        <label className="text-gray-500 text-xs block mb-1">Category</label>
                        <select
                          value={txn.category}
                          onChange={(e) => updateTransaction(idx, 'category', e.target.value)}
                          className="w-full bg-black border border-gray-600 rounded-lg px-2 py-1.5 text-white text-sm"
                        >
                          {categoryOptions.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.icon} {cat.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Type */}
                      <div className="w-28 flex-shrink-0">
                        <label className="text-gray-500 text-xs block mb-1">Type</label>
                        <select
                          value={txn.type}
                          onChange={(e) => updateTransaction(idx, 'type', e.target.value)}
                          className="w-full bg-black border border-gray-600 rounded-lg px-2 py-1.5 text-white text-sm"
                        >
                          <option value="expense">Expense</option>
                          <option value="income">Income</option>
                        </select>
                      </div>

                      {/* Delete */}
                      <button
                        onClick={() => removeTransaction(idx)}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded-lg mt-5"
                        title="Remove transaction"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-700 flex-shrink-0">
              <div className="flex items-center justify-between">
                <p className="text-gray-400 text-sm">
                  Total: <span className="text-white font-medium">
                    ${parsedTransactions.reduce((sum, t) => sum + (t.type === 'income' ? t.amount : -t.amount), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </p>
                <button
                  onClick={() => { setShowPreview(false); setParsedTransactions([]); }}
                  className="text-gray-400 hover:text-white text-sm"
                >
                  Discard All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Transactions Panel Component for Tax Purposes
const TransactionsPanel = ({ userId }) => {
  const [transactions, setTransactions] = useState([]);
  const [uploadedTransactions, setUploadedTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: '', // Empty = show all
    end: ''    // Empty = show all
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [activeSource, setActiveSource] = useState('all'); // 'all', 'bank', 'uploaded'
  const [activeTag, setActiveTag] = useState('all'); // 'all', 'business', 'personal', 'cash'
  const [activeType, setActiveType] = useState('all'); // 'all', 'income', 'expense'
  
  // Sorting state for the main transaction table
  const [tableSortField, setTableSortField] = useState('date'); // 'date', 'description', 'category', 'type', 'tag', 'source', 'amount'
  const [tableSortDirection, setTableSortDirection] = useState('desc'); // 'asc', 'desc'
  
  // Toggle sort direction or change sort field
  // Text fields default to asc (A→Z), numeric/date fields default to desc (newest/largest first)
  const TEXT_SORT_FIELDS = ['description', 'category', 'type', 'tag', 'source'];
  const handleTableSort = (field) => {
    if (tableSortField === field) {
      setTableSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setTableSortField(field);
      setTableSortDirection(TEXT_SORT_FIELDS.includes(field) ? 'asc' : 'desc');
    }
  };
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 100;
  
  // Bulk selection state
  const [selectedTransactions, setSelectedTransactions] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkTag, setBulkTag] = useState('');
  
  // Manual add transaction modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTransaction, setNewTransaction] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    amount: '',
    category: 'other',
    type: 'expense',
    tag: 'personal'
  });
  const [saving, setSaving] = useState(false);

  // Edit transaction modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editForm, setEditForm] = useState({
    date: '',
    description: '',
    amount: '',
    category: 'other',
    type: 'expense',
    tag: 'personal'
  });

  // Handle click on transaction row to edit
  const handleEditTransaction = (txn) => {
    // Only allow editing uploaded transactions (not bank transactions)
    if (txn.source !== 'uploaded') {
      toast.info('Bank transactions cannot be edited');
      return;
    }
    
    setEditingTransaction(txn);
    setEditForm({
      date: txn.date || '',
      description: txn.name || txn.description || '',
      amount: Math.abs(txn.amount) || '',
      category: Array.isArray(txn.category) ? txn.category[0] : (txn.category || 'other'),
      type: txn.type || 'expense',
      tag: txn.tag || 'personal'
    });
    setShowEditModal(true);
  };

  // Toggle transaction selection for bulk edit
  const toggleTransactionSelection = (txnId, e) => {
    e.stopPropagation();
    setSelectedTransactions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(txnId)) {
        newSet.delete(txnId);
      } else {
        newSet.add(txnId);
      }
      return newSet;
    });
  };

  // Select all visible transactions
  const selectAllTransactions = () => {
    const uploadedTxns = filteredTransactions.filter(t => t.source === 'uploaded');
    if (selectedTransactions.size === uploadedTxns.length) {
      setSelectedTransactions(new Set());
    } else {
      setSelectedTransactions(new Set(uploadedTxns.map(t => t.id || t.transaction_id)));
    }
  };

  // Apply bulk action
  const applyBulkAction = async () => {
    if (selectedTransactions.size === 0) {
      toast.error('No transactions selected');
      return;
    }

    setSaving(true);
    try {
      const transactionIds = Array.from(selectedTransactions);
      
      if (bulkAction === 'delete') {
        // Delete all selected
        for (const txnId of transactionIds) {
          await fetch(`${API_URL}/api/transaction-upload/delete`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ transaction_id: txnId, user_id: userId })
          });
        }
        toast.success(`Deleted ${transactionIds.length} transactions`);
      } else if (bulkAction === 'category' && bulkCategory) {
        // Update category for all selected
        for (const txnId of transactionIds) {
          await fetch(`${API_URL}/api/transaction-upload/update`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              transaction_id: txnId, 
              user_id: userId,
              category: bulkCategory
            })
          });
        }
        toast.success(`Updated category for ${transactionIds.length} transactions`);
      } else if (bulkAction === 'tag' && bulkTag) {
        // Update tag for all selected
        for (const txnId of transactionIds) {
          await fetch(`${API_URL}/api/transaction-upload/update`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              transaction_id: txnId, 
              user_id: userId,
              tag: bulkTag
            })
          });
        }
        toast.success(`Updated tag for ${transactionIds.length} transactions`);
      }

      setSelectedTransactions(new Set());
      setShowBulkActions(false);
      setBulkAction('');
      setBulkCategory('');
      setBulkTag('');
      fetchUploadedTransactions();
    } catch (err) {
      toast.error(`Bulk action failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Save edited transaction
  const handleSaveEdit = async () => {
    if (!editForm.description.trim() || !editForm.amount) {
      toast.error('Please fill in description and amount');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/transaction-upload/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transaction_id: editingTransaction.id || editingTransaction.transaction_id,
          user_id: userId,
          date: editForm.date,
          description: editForm.description,
          amount: parseFloat(editForm.amount),
          category: editForm.category,
          type: editForm.type,
          tag: editForm.tag
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to update');

      toast.success('Transaction updated successfully!');
      setShowEditModal(false);
      setEditingTransaction(null);
      fetchUploadedTransactions();
    } catch (err) {
      console.error('Update transaction error:', err);
      toast.error(`Failed to update: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Delete transaction
  const handleDeleteTransaction = async () => {
    if (!editingTransaction) return;
    
    if (!window.confirm('Are you sure you want to delete this transaction?')) {
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/transaction-upload/delete`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transaction_id: editingTransaction.id || editingTransaction.transaction_id,
          user_id: userId
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to delete');

      toast.success('Transaction deleted!');
      setShowEditModal(false);
      setEditingTransaction(null);
      fetchUploadedTransactions();
    } catch (err) {
      console.error('Delete transaction error:', err);
      toast.error(`Failed to delete: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Default category tabs (Income removed - using type filter instead)
  const defaultCategories = [
    { id: 'all', name: 'All', icon: '📊', keywords: [], isDefault: true },
    { id: 'food', name: 'Food', icon: '🍔', keywords: ['restaurant', 'food', 'eat', 'coffee', 'pizza', 'burger', 'mcdonald', 'starbucks', 'doordash', 'uber eats', 'grubhub', 'grocery'], isDefault: true },
    { id: 'gas', name: 'Gas', icon: '⛽', keywords: ['gas', 'fuel', 'shell', 'exxon', 'chevron', 'bp', 'texaco'], isDefault: true },
    { id: 'studio', name: 'Studio', icon: '🎬', keywords: ['studio', 'peerspace', 'rehearsal', 'recording', 'production'], isDefault: true },
    { id: 'rent', name: 'Rent', icon: '🏠', keywords: ['rent', 'lease', 'apartment', 'housing', 'mortgage'], isDefault: true },
    { id: 'utilities', name: 'Utilities', icon: '💡', keywords: ['electric', 'water', 'utility', 'power', 'energy'], isDefault: true },
    { id: 'shopping', name: 'Shopping', icon: '🛒', keywords: ['amazon', 'walmart', 'target', 'costco', 'best buy', 'ebay', 'shop'], isDefault: true },
    { id: 'travel', name: 'Travel', icon: '✈️', keywords: ['airline', 'hotel', 'airbnb', 'flight', 'parking', 'toll'], isDefault: true },
    { id: 'subscription', name: 'Subscriptions', icon: '📱', keywords: ['subscription', 'adobe', 'dropbox', 'microsoft', 'netflix', 'spotify'], isDefault: true },
    { id: 'other', name: 'Other', icon: '📋', keywords: [], isDefault: true }
  ];

  // Custom categories from database
  const [customCategories, setCustomCategories] = useState([]);
  const [modifiedKeywords, setModifiedKeywords] = useState({});
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);

  // Category management state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategoryForm, setNewCategoryForm] = useState({ name: '', icon: '📁', keywords: '' });
  const [isScanning, setIsScanning] = useState(false);
  
  // Receipt preview state
  const [receiptPreview, setReceiptPreview] = useState(null); // { url, name }
  const [uploadingReceipt, setUploadingReceipt] = useState(null); // transaction ID being uploaded

  // Combined category tabs (default + custom)
  const categoryTabs = [...defaultCategories, ...customCategories];

  // Load custom categories from database on mount
  useEffect(() => {
    if (userId && !categoriesLoaded) {
      loadCustomCategories();
    }
  }, [userId, categoriesLoaded]);

  const loadCustomCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/transaction-upload/custom-categories?user_id=${userId}`);
      if (response.ok) {
        const data = await response.json();
        setCustomCategories(data.categories || []);
        setModifiedKeywords(data.modified_keywords || {});
        setCategoriesLoaded(true);
      }
    } catch (err) {
      console.error('Error loading custom categories:', err);
    }
  };

  // Save custom categories to database
  const saveCategoriesToDatabase = async (categories, keywords = modifiedKeywords) => {
    try {
      await fetch(`${API_URL}/api/transaction-upload/custom-categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          categories: categories,
          modified_keywords: keywords
        })
      });
    } catch (err) {
      console.error('Error saving categories to database:', err);
    }
  };

  // Add new category
  const handleAddCategory = async () => {
    if (!newCategoryForm.name.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    
    const categoryId = newCategoryForm.name.toLowerCase().replace(/\s+/g, '_');
    
    // Check if category already exists
    if (categoryTabs.some(c => c.id === categoryId)) {
      toast.error('Category already exists');
      return;
    }
    
    const newCategory = {
      id: categoryId,
      name: newCategoryForm.name.trim(),
      icon: newCategoryForm.icon || '📁',
      keywords: newCategoryForm.keywords.split(',').map(k => k.trim().toLowerCase()).filter(k => k),
      isDefault: false
    };
    
    const updatedCategories = [...customCategories, newCategory];
    setCustomCategories(updatedCategories);
    await saveCategoriesToDatabase(updatedCategories);
    setNewCategoryForm({ name: '', icon: '📁', keywords: '' });
    toast.success(`Category "${newCategory.name}" added`);
  };

  // Edit category
  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setNewCategoryForm({
      name: category.name,
      icon: category.icon,
      keywords: (modifiedKeywords[category.id] || category.keywords || []).join(', ')
    });
    setShowCategoryModal(true);
  };

  // Save edited category
  const handleSaveCategory = async () => {
    if (!newCategoryForm.name.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    
    if (editingCategory) {
      // Update existing category
      if (editingCategory.isDefault) {
        // For default categories, we can only update keywords
        const newKeywords = newCategoryForm.keywords.split(',').map(k => k.trim().toLowerCase()).filter(k => k);
        const updatedModifiedKeywords = {
          ...modifiedKeywords,
          [editingCategory.id]: newKeywords
        };
        setModifiedKeywords(updatedModifiedKeywords);
        await saveCategoriesToDatabase(customCategories, updatedModifiedKeywords);
        toast.success(`Keywords updated for "${editingCategory.name}"`);
      } else {
        // Update custom category
        const updatedCategories = customCategories.map(c => 
          c.id === editingCategory.id 
            ? {
                ...c,
                name: newCategoryForm.name.trim(),
                icon: newCategoryForm.icon,
                keywords: newCategoryForm.keywords.split(',').map(k => k.trim().toLowerCase()).filter(k => k)
              }
            : c
        );
        setCustomCategories(updatedCategories);
        await saveCategoriesToDatabase(updatedCategories);
        toast.success(`Category "${newCategoryForm.name}" updated`);
      }
    } else {
      // Add new category
      await handleAddCategory();
    }
    
    setShowCategoryModal(false);
    setEditingCategory(null);
    setNewCategoryForm({ name: '', icon: '📁', keywords: '' });
  };

  // Delete custom category
  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    const updatedCategories = customCategories.filter(c => c.id !== categoryId);
    setCustomCategories(updatedCategories);
    await saveCategoriesToDatabase(updatedCategories);
    toast.success('Category deleted');
  };

  // Scan and re-categorize all transactions
  const scanAndCategorize = async () => {
    if (uploadedTransactions.length === 0) {
      toast.info('No uploaded transactions to scan');
      return;
    }
    
    setIsScanning(true);
    let categorizedCount = 0;
    
    // Build keyword map with all categories (using state-based modifiedKeywords)
    const keywordMap = {};
    categoryTabs.forEach(cat => {
      if (cat.id === 'all') return;
      const keywords = modifiedKeywords[cat.id] || cat.keywords || [];
      keywords.forEach(keyword => {
        if (keyword) keywordMap[keyword.toLowerCase()] = cat.id;
      });
    });
    
    try {
      for (const txn of uploadedTransactions) {
        const description = (txn.name || txn.description || '').toLowerCase();
        let newCategory = null;
        
        // Find matching category based on keywords
        for (const [keyword, categoryId] of Object.entries(keywordMap)) {
          if (description.includes(keyword)) {
            newCategory = categoryId;
            break;
          }
        }
        
        // Update if category changed
        if (newCategory && newCategory !== (Array.isArray(txn.category) ? txn.category[0] : txn.category)) {
          await fetch(`${API_URL}/api/transaction-upload/update`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              transaction_id: txn.id || txn.transaction_id,
              user_id: userId,
              category: newCategory
            })
          });
          categorizedCount++;
        }
      }
      
      toast.success(`Scanned ${uploadedTransactions.length} transactions, re-categorized ${categorizedCount}`);
      fetchUploadedTransactions();
    } catch (err) {
      toast.error(`Scan failed: ${err.message}`);
    } finally {
      setIsScanning(false);
    }
  };

  // Fetch uploaded transactions from database on mount
  useEffect(() => {
    if (userId) {
      fetchUploadedTransactions();
    }
  }, [userId]);

  const fetchUploadedTransactions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/finances/transactions?user_id=${userId}`);
      if (response.ok) {
        const data = await response.json();
        // Transform to match the transaction format
        const transformed = (data.transactions || []).map(t => ({
          ...t,
          name: t.description,
          transaction_id: t.id,
          category: [t.category || 'other'],
          source: 'uploaded'
        }));
        setUploadedTransactions(transformed);
      }
    } catch (err) {
      console.error('Error fetching uploaded transactions:', err);
    }
  };

  const fetchTransactions = async () => {
    if (!userId) {
      toast.error('Please log in to view transactions');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/plaid/transactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          start_date: dateRange.start,
          end_date: dateRange.end
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to fetch transactions');
      }
      
      // Add source tag to bank transactions
      const bankTransactions = (data.transactions || []).map(t => ({
        ...t,
        source: 'bank'
      }));
      
      setTransactions(bankTransactions);
      setSummary(data.summary || null);
      toast.success(`Loaded ${data.total_count} bank transactions`);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError(err.message);
      // Don't show error toast if we have uploaded transactions
      if (uploadedTransactions.length === 0) {
        toast.error(`Failed to load bank transactions: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle receipt upload for a transaction
  const handleReceiptUpload = async (transactionId, file) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload an image or PDF file');
      return;
    }
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setUploadingReceipt(transactionId);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('transaction_id', transactionId);
      formData.append('user_id', userId);
      
      const response = await fetch(`${API_URL}/api/finances/receipt/upload`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.url) {
        // Update the transaction with the receipt URL
        setUploadedTransactions(prev => 
          prev.map(t => t.id === transactionId ? { ...t, receipt_url: data.url } : t)
        );
        setTransactions(prev => 
          prev.map(t => t.id === transactionId ? { ...t, receipt_url: data.url } : t)
        );
        toast.success('Receipt uploaded successfully!');
      } else {
        toast.error(data.error || 'Failed to upload receipt');
      }
    } catch (err) {
      console.error('Receipt upload error:', err);
      toast.error('Failed to upload receipt');
    } finally {
      setUploadingReceipt(null);
    }
  };

  // Combine all transactions based on source filter
  const allTransactions = activeSource === 'bank' 
    ? transactions 
    : activeSource === 'uploaded' 
      ? uploadedTransactions 
      : [...transactions, ...uploadedTransactions];

  // Get unique categories for filter from all transactions
  const categories = ['all', ...new Set(allTransactions.flatMap(t => {
    if (Array.isArray(t.category)) return t.category;
    return [t.category || 'Uncategorized'];
  }))];
  
  // Filter transactions
  const filteredTransactions = allTransactions.filter(t => {
    const name = t.name || t.description || '';
    const merchant = t.merchant_name || '';
    const matchesSearch = searchQuery === '' || 
      name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      merchant.toLowerCase().includes(searchQuery.toLowerCase());
    
    const txnCategories = Array.isArray(t.category) ? t.category : [t.category || 'other'];
    const matchesCategory = categoryFilter === 'all' || 
      txnCategories.some(c => c.toLowerCase() === categoryFilter.toLowerCase());
    
    const txnTag = t.tag || 'personal';
    const matchesTag = activeTag === 'all' || txnTag === activeTag;
    
    // Type filter (income/expense)
    const txnType = t.type || 'expense';
    const matchesType = activeType === 'all' || txnType === activeType;
    
    // Date range filter
    const txnDate = t.date ? new Date(t.date) : null;
    const startDate = dateRange.start ? new Date(dateRange.start) : null;
    const endDate = dateRange.end ? new Date(dateRange.end + 'T23:59:59') : null;
    
    const matchesDateRange = !txnDate || (
      (!startDate || txnDate >= startDate) && 
      (!endDate || txnDate <= endDate)
    );
    
    return matchesSearch && matchesCategory && matchesTag && matchesType && matchesDateRange;
  }).sort((a, b) => {
    let aVal, bVal;
    
    switch (tableSortField) {
      case 'date':
        aVal = a.date ? new Date(a.date).getTime() : 0;
        bVal = b.date ? new Date(b.date).getTime() : 0;
        break;
      case 'description':
        aVal = (a.name || a.description || '').toLowerCase().trim();
        bVal = (b.name || b.description || '').toLowerCase().trim();
        break;
      case 'category':
        aVal = (Array.isArray(a.category) ? a.category[0] : (a.category || '')).toLowerCase().trim();
        bVal = (Array.isArray(b.category) ? b.category[0] : (b.category || '')).toLowerCase().trim();
        break;
      case 'type':
        aVal = (a.type || '').toLowerCase().trim();
        bVal = (b.type || '').toLowerCase().trim();
        break;
      case 'tag':
        aVal = (a.tag || 'personal').toLowerCase().trim();
        bVal = (b.tag || 'personal').toLowerCase().trim();
        break;
      case 'source':
        aVal = (a.source || '').toLowerCase().trim();
        bVal = (b.source || '').toLowerCase().trim();
        break;
      case 'amount':
        aVal = Math.abs(Number(a.amount) || 0);
        bVal = Math.abs(Number(b.amount) || 0);
        break;
      default:
        aVal = a.date ? new Date(a.date).getTime() : 0;
        bVal = b.date ? new Date(b.date).getTime() : 0;
    }

    if (aVal === bVal) return 0;
    const cmp = aVal > bVal ? 1 : -1;
    return tableSortDirection === 'asc' ? cmp : -cmp;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredTransactions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedTransactions = filteredTransactions.slice(startIndex, endIndex);

  // Reset to page 1 when filters or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, categoryFilter, activeSource, activeTag, activeType, dateRange.start, dateRange.end, tableSortField, tableSortDirection]);

  // Page navigation functions
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll to top of table
      document.querySelector('[data-testid="transactions-table"]')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 10; // Show more page numbers
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      // Always show first page
      pages.push(1);
      
      // Calculate start and end of middle section
      let startPage = Math.max(2, currentPage - 3);
      let endPage = Math.min(totalPages - 1, currentPage + 3);
      
      // Adjust if we're near the beginning
      if (currentPage <= 5) {
        startPage = 2;
        endPage = Math.min(8, totalPages - 1);
      }
      
      // Adjust if we're near the end
      if (currentPage >= totalPages - 4) {
        startPage = Math.max(2, totalPages - 7);
        endPage = totalPages - 1;
      }
      
      // Add ellipsis before middle section if needed
      if (startPage > 2) {
        pages.push('...');
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add ellipsis after middle section if needed
      if (endPage < totalPages - 1) {
        pages.push('...');
      }
      
      // Always show last page
      pages.push(totalPages);
    }
    return pages;
  };

  // Export to CSV for taxes
  const exportToCSV = () => {
    const headers = ['Date', 'Description', 'Merchant', 'Amount', 'Category', 'Type', 'Source'];
    const rows = filteredTransactions.map(t => {
      const cats = Array.isArray(t.category) ? t.category.join(', ') : (t.category || 'other');
      return [
        t.date,
        t.name || t.description || '',
        t.merchant_name || '',
        t.amount,
        cats,
        t.type || 'expense',
        t.source || 'unknown'
      ];
    });
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${dateRange.start}_to_${dateRange.end}.csv`;
    a.click();
    toast.success('Transactions exported to CSV');
  };

  const COLORS = ['#8B5CF6', '#EC4899', '#06B6D4', '#10B981', '#F59E0B', '#EF4444'];

  // Handle manual transaction save
  const handleSaveTransaction = async () => {
    if (!newTransaction.description.trim() || !newTransaction.amount) {
      toast.error('Please fill in description and amount');
      return;
    }

    if (!userId) {
      toast.error('Please log in to add transactions');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/transaction-upload/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          transactions: [{
            date: newTransaction.date,
            description: newTransaction.description,
            amount: parseFloat(newTransaction.amount),
            category: newTransaction.category,
            type: newTransaction.type,
            tag: newTransaction.tag
          }]
        })
      });

      // Clone response before reading body to avoid "body already used" error
      const responseClone = response.clone();
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        // If JSON parse fails, try to get text from clone
        const text = await responseClone.text();
        console.error('Response parsing failed:', text);
        throw new Error('Invalid server response');
      }

      if (!response.ok) throw new Error(data.detail || 'Failed to save');

      toast.success('Transaction added successfully!');
      setShowAddModal(false);
      setNewTransaction({
        date: new Date().toISOString().split('T')[0],
        description: '',
        amount: '',
        category: 'other',
        type: 'expense',
        tag: 'personal'
      });
      // Refresh the list
      fetchUploadedTransactions();
    } catch (err) {
      console.error('Save transaction error:', err);
      toast.error(`Failed to add transaction: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-black border border-gray-700 rounded-2xl p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Receipt size={18} className="text-purple-400" />
            Transaction History
          </h3>
          <p className="text-gray-500 text-sm">View and export transactions for tax purposes</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-xl font-medium transition-colors"
            data-testid="add-transaction-btn"
          >
            <Plus size={16} />
            Add Transaction
          </button>
          <button
            onClick={fetchTransactions}
            disabled={loading}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 text-white px-4 py-2 rounded-xl font-medium transition-colors"
            data-testid="fetch-bank-transactions-btn"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            {loading ? 'Loading...' : 'Sync Bank'}
          </button>
          <button
            onClick={fetchUploadedTransactions}
            className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-xl font-medium transition-colors"
            data-testid="refresh-uploaded-btn"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          {allTransactions.length > 0 && (
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-xl font-medium transition-colors"
              data-testid="export-csv-btn"
            >
              <Download size={16} />
              Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Source Tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { id: 'all', label: 'All Sources', count: transactions.length + uploadedTransactions.length },
          { id: 'bank', label: 'Bank', count: transactions.length },
          { id: 'uploaded', label: 'Uploaded', count: uploadedTransactions.length }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSource(tab.id)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              activeSource === tab.id
                ? 'bg-purple-500 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
            data-testid={`source-tab-${tab.id}`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
        
        {/* Divider */}
        <div className="w-px bg-gray-700 mx-2"></div>
        
        {/* Business/Personal/Cash Tabs */}
        {[
          { id: 'all', label: 'All', icon: '📋' },
          { id: 'business', label: 'Business', icon: '💼' },
          { id: 'personal', label: 'Personal', icon: '👤' },
          { id: 'cash', label: 'Cash', icon: '💵' }
        ].map(tab => {
          const count = allTransactions.filter(t => tab.id === 'all' || (t.tag || 'personal') === tab.id).length;
          return (
            <button
              key={`tag-${tab.id}`}
              onClick={() => setActiveTag(tab.id)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                activeTag === tab.id
                  ? 'bg-cyan-500 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
              data-testid={`tag-tab-${tab.id}`}
            >
              <span>{tab.icon}</span>
              {tab.label}
              {count > 0 && <span className="text-xs opacity-70">({count})</span>}
            </button>
          );
        })}
        
        {/* Divider */}
        <div className="w-px bg-gray-700 mx-2"></div>
        
        {/* Income/Expense Type Tabs */}
        {[
          { id: 'all', label: 'All', icon: '📊' },
          { id: 'income', label: 'Income', icon: '💰' },
          { id: 'expense', label: 'Expenses', icon: '💸' }
        ].map(tab => {
          const count = allTransactions.filter(t => tab.id === 'all' || (t.type || 'expense') === tab.id).length;
          return (
            <button
              key={`type-${tab.id}`}
              onClick={() => setActiveType(tab.id)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                activeType === tab.id
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
              data-testid={`type-tab-${tab.id}`}
            >
              <span>{tab.icon}</span>
              {tab.label}
              {count > 0 && <span className="text-xs opacity-70">({count})</span>}
            </button>
          );
        })}
      </div>

      {/* Category Tabs with Management */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-400 text-sm font-medium">Categories</span>
          <div className="flex items-center gap-2">
            <button
              onClick={scanAndCategorize}
              disabled={isScanning}
              className="flex items-center gap-1.5 text-xs bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 text-white px-3 py-1.5 rounded-lg font-medium transition-colors"
              data-testid="scan-categories-btn"
            >
              {isScanning ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              {isScanning ? 'Scanning...' : 'Scan'}
            </button>
            <button
              onClick={() => { setEditingCategory(null); setNewCategoryForm({ name: '', icon: '📁', keywords: '' }); setShowCategoryModal(true); }}
              className="flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded-lg font-medium transition-colors"
              data-testid="add-category-btn"
            >
              <Plus size={12} />
              Add Category
            </button>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {categoryTabs.map(cat => {
            const count = allTransactions.filter(t => {
              const txnCats = Array.isArray(t.category) ? t.category : [t.category || 'other'];
              return cat.id === 'all' || txnCats.some(c => c.toLowerCase() === cat.id);
            }).length;
            
            return (
              <div key={cat.id} className="relative group">
                <button
                  onClick={() => setCategoryFilter(cat.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    categoryFilter === cat.id
                      ? 'bg-emerald-500 text-white'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                  data-testid={`category-tab-${cat.id}`}
                >
                  <span>{cat.icon}</span>
                  {cat.name}
                  {count > 0 && <span className="ml-1 text-xs opacity-70">({count})</span>}
                </button>
                {/* Edit/Delete buttons on hover (not for 'all' tab) */}
                {cat.id !== 'all' && (
                  <div className="absolute -top-2 -right-2 hidden group-hover:flex gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleEditCategory(cat); }}
                      className="w-5 h-5 bg-blue-500 hover:bg-blue-400 rounded-full flex items-center justify-center text-white"
                      title="Edit category"
                    >
                      <Pencil size={10} />
                    </button>
                    {!cat.isDefault && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat.id); }}
                        className="w-5 h-5 bg-red-500 hover:bg-red-400 rounded-full flex items-center justify-center text-white"
                        title="Delete category"
                      >
                        <X size={10} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="flex flex-wrap gap-4 mb-4 items-center">
        <div className="flex items-center gap-2">
          <label className="text-gray-400 text-sm">From:</label>
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
            data-testid="date-from"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-gray-400 text-sm">To:</label>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
            data-testid="date-to"
          />
        </div>
        {/* Quick Date Presets */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => {
              const today = new Date();
              const last30 = new Date(today);
              last30.setDate(last30.getDate() - 30);
              setDateRange({
                start: last30.toISOString().split('T')[0],
                end: today.toISOString().split('T')[0]
              });
            }}
            className="text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 px-2 py-1 rounded"
          >
            30 Days
          </button>
          <button
            onClick={() => {
              const today = new Date();
              const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
              setDateRange({
                start: firstOfMonth.toISOString().split('T')[0],
                end: today.toISOString().split('T')[0]
              });
            }}
            className="text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 px-2 py-1 rounded"
          >
            This Month
          </button>
          <button
            onClick={() => {
              const today = new Date();
              const thisYear = new Date(today.getFullYear(), 0, 1);
              setDateRange({
                start: thisYear.toISOString().split('T')[0],
                end: today.toISOString().split('T')[0]
              });
            }}
            className="text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 px-2 py-1 rounded"
          >
            This Year
          </button>
          <button
            onClick={() => {
              const today = new Date();
              const lastYear = new Date(today.getFullYear() - 1, 0, 1);
              const endLastYear = new Date(today.getFullYear() - 1, 11, 31);
              setDateRange({
                start: lastYear.toISOString().split('T')[0],
                end: endLastYear.toISOString().split('T')[0]
              });
            }}
            className="text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 px-2 py-1 rounded"
          >
            Last Year
          </button>
          <button
            onClick={() => {
              setDateRange({
                start: '',
                end: ''
              });
            }}
            className="text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 px-2 py-1 rounded"
          >
            All Time
          </button>
        </div>
        <div className="flex items-center gap-2">
          <Search size={16} className="text-gray-400" />
          <input
            type="text"
            placeholder="Search transactions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm w-48"
          />
        </div>
        {categories.length > 1 && (
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat === 'all' ? 'All Categories' : cat}</option>
            ))}
          </select>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-gradient-to-br from-green-900/50 to-green-800/50 border border-green-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 text-green-400 mb-1">
              <ArrowDownRight size={16} />
              <span className="text-sm">Total Income</span>
            </div>
            <p className="text-2xl font-bold text-white">${summary.total_income?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="bg-gradient-to-br from-red-900/50 to-red-800/50 border border-red-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 text-red-400 mb-1">
              <ArrowUpRight size={16} />
              <span className="text-sm">Total Expenses</span>
            </div>
            <p className="text-2xl font-bold text-white">${summary.total_expenses?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="bg-gradient-to-br from-purple-900/50 to-purple-800/50 border border-purple-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 text-purple-400 mb-1">
              <DollarSign size={16} />
              <span className="text-sm">Net</span>
            </div>
            <p className={`text-2xl font-bold ${summary.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              ${summary.net?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </div>
          <div className="bg-gradient-to-br from-cyan-900/50 to-cyan-800/50 border border-cyan-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 text-cyan-400 mb-1">
              <FileText size={16} />
              <span className="text-sm">Transactions</span>
            </div>
            <p className="text-2xl font-bold text-white">{filteredTransactions.length}</p>
          </div>
        </div>
      )}

      {/* Expense by Category Chart */}
      {summary?.expense_by_category && Object.keys(summary.expense_by_category).length > 0 && (
        <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-4 mb-4">
          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
            <Filter size={16} className="text-purple-400" />
            Expenses by Category
          </h4>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={Object.entries(summary.expense_by_category).map(([name, value]) => ({ name, value }))}>
                <XAxis dataKey="name" tick={{ fill: '#9CA3AF', fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                <YAxis tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(value) => [`$${value.toFixed(2)}`, 'Amount']}
                />
                <Bar dataKey="value" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/30 border border-red-500/30 rounded-xl p-4 mb-4">
          <p className="text-red-400">{error}</p>
          <p className="text-gray-400 text-sm mt-1">Make sure you have connected a bank account first.</p>
        </div>
      )}

      {/* Transactions Table */}
      {filteredTransactions.length > 0 ? (
        <div className="overflow-x-auto">
          {/* Totals Summary Bar */}
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-700">
            <div className="flex items-center gap-4">
              <span className="text-gray-400 text-sm">
                Showing <span className="text-white font-medium">{startIndex + 1}-{Math.min(endIndex, filteredTransactions.length)}</span> of <span className="text-white font-medium">{filteredTransactions.length}</span> transactions
                {totalPages > 1 && <span className="text-gray-500 ml-2">(Page {currentPage} of {totalPages})</span>}
              </span>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <span className="text-gray-500 text-xs block">Income</span>
                <span className="text-green-400 font-semibold">
                  +${filteredTransactions
                    .filter(t => t.type === 'income')
                    .reduce((sum, t) => sum + Math.abs(t.amount), 0)
                    .toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="text-right">
                <span className="text-gray-500 text-xs block">Expenses</span>
                <span className="text-red-400 font-semibold">
                  -${filteredTransactions
                    .filter(t => t.type !== 'income')
                    .reduce((sum, t) => sum + Math.abs(t.amount), 0)
                    .toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="text-right pl-4 border-l border-gray-700">
                <span className="text-gray-500 text-xs block">Net Total</span>
                <span className={`font-bold text-lg ${
                  filteredTransactions.reduce((sum, t) => {
                    const isIncome = t.type === 'income';
                    return sum + (isIncome ? Math.abs(t.amount) : -Math.abs(t.amount));
                  }, 0) >= 0 ? 'text-green-400' : 'text-red-400'
                }`} data-testid="transactions-total">
                  ${Math.abs(filteredTransactions.reduce((sum, t) => {
                    const isIncome = t.type === 'income';
                    return sum + (isIncome ? Math.abs(t.amount) : -Math.abs(t.amount));
                  }, 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>
          
          {/* Bulk Actions Bar */}
          {selectedTransactions.size > 0 && (
            <div className="bg-purple-900/30 border border-purple-500/30 rounded-xl p-3 mb-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-purple-300 font-medium">
                  {selectedTransactions.size} selected
                </span>
                <button
                  onClick={() => setSelectedTransactions(new Set())}
                  className="text-gray-400 hover:text-white text-sm"
                >
                  Clear
                </button>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={bulkAction}
                  onChange={(e) => setBulkAction(e.target.value)}
                  className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-1.5 text-white text-sm"
                >
                  <option value="">Select action...</option>
                  <option value="category">Change Category</option>
                  <option value="tag">Change Tag</option>
                  <option value="delete">Delete Selected</option>
                </select>
                
                {bulkAction === 'category' && (
                  <select
                    value={bulkCategory}
                    onChange={(e) => setBulkCategory(e.target.value)}
                    className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-1.5 text-white text-sm"
                    data-testid="bulk-category-select"
                  >
                    <option value="">Select category...</option>
                    {categoryTabs.filter(c => c.id !== 'all').map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                    ))}
                  </select>
                )}
                
                {bulkAction === 'tag' && (
                  <select
                    value={bulkTag}
                    onChange={(e) => setBulkTag(e.target.value)}
                    className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-1.5 text-white text-sm"
                    data-testid="bulk-tag-select"
                  >
                    <option value="">Select tag...</option>
                    <option value="business">💼 Business</option>
                    <option value="personal">👤 Personal</option>
                    <option value="cash">💵 Cash</option>
                  </select>
                )}
                
                <button
                  onClick={applyBulkAction}
                  disabled={saving || !bulkAction || (bulkAction === 'category' && !bulkCategory) || (bulkAction === 'tag' && !bulkTag)}
                  className="bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium"
                  data-testid="apply-bulk-action-btn"
                >
                  {saving ? 'Applying...' : 'Apply'}
                </button>
              </div>
            </div>
          )}
          
          <table className="w-full text-sm" data-testid="transactions-table">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left text-gray-400 font-medium py-3 px-2 w-10">
                  <input
                    type="checkbox"
                    checked={selectedTransactions.size > 0 && selectedTransactions.size === filteredTransactions.filter(t => t.source === 'uploaded').length}
                    onChange={selectAllTransactions}
                    className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-purple-500 focus:ring-purple-500"
                    data-testid="select-all-checkbox"
                  />
                </th>
                {[
                  { field: 'date',        label: 'Date',        align: 'left'  },
                  { field: 'description', label: 'Description', align: 'left'  },
                  { field: 'category',    label: 'Category',    align: 'left'  },
                  { field: 'type',        label: 'Type',        align: 'left'  },
                  { field: 'tag',         label: 'Tag',         align: 'left'  },
                  { field: 'source',      label: 'Source',      align: 'left'  },
                  { field: 'amount',      label: 'Amount',      align: 'right' },
                ].map(({ field, label, align }) => {
                  const isActive = tableSortField === field;
                  const SortIcon = isActive
                    ? (tableSortDirection === 'asc' ? ArrowUp : ArrowDown)
                    : ArrowUpDown;
                  return (
                    <th key={field} className={`text-${align} py-3 px-2`}>
                      <button
                        onClick={() => handleTableSort(field)}
                        className={`font-medium flex items-center gap-1 transition-colors select-none ${align === 'right' ? 'justify-end w-full' : ''} ${
                          isActive ? 'text-cyan-400' : 'text-gray-400 hover:text-white'
                        }`}
                        data-testid={`sort-table-${field}`}
                        title={`Sort by ${label}`}
                      >
                        {label}
                        <SortIcon size={12} className={isActive ? 'text-cyan-400' : 'text-gray-600'} />
                      </button>
                    </th>
                  );
                })}
                <th className="text-center py-3 px-2">
                  <span className="font-medium text-gray-400">Receipt</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedTransactions.map((txn, idx) => {
                const displayName = txn.name || txn.description || 'Unknown';
                const displayCategory = Array.isArray(txn.category) 
                  ? txn.category[0] 
                  : (txn.category || 'other');
                const isIncome = txn.type === 'income';
                const displayAmount = Math.abs(txn.amount);
                const isEditable = txn.source === 'uploaded';
                const txnId = txn.id || txn.transaction_id;
                const isSelected = selectedTransactions.has(txnId);
                const displayTag = txn.tag || 'personal';
                const globalIdx = startIndex + idx;
                
                return (
                  <tr 
                    key={txnId || globalIdx} 
                    className={`border-b border-gray-800 hover:bg-gray-900/50 ${isEditable ? 'cursor-pointer' : ''} ${isSelected ? 'bg-purple-900/20' : ''}`}
                    onClick={() => handleEditTransaction(txn)}
                    data-testid={`transaction-row-${globalIdx}`}
                  >
                    <td className="py-3 px-2" onClick={(e) => e.stopPropagation()}>
                      {isEditable && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => toggleTransactionSelection(txnId, e)}
                          className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-purple-500 focus:ring-purple-500"
                          data-testid={`checkbox-${globalIdx}`}
                        />
                      )}
                    </td>
                    <td className="py-3 px-2 text-gray-300 whitespace-nowrap">{txn.date}</td>
                    <td className="py-3 px-2">
                      <div className="text-white flex items-center gap-2">
                        {displayName}
                        {isEditable && <Pencil size={12} className="text-gray-500" />}
                      </div>
                      {txn.merchant_name && (
                        <div className="text-gray-500 text-xs">{txn.merchant_name}</div>
                      )}
                    </td>
                    <td className="py-3 px-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        displayCategory === 'food' ? 'bg-orange-500/20 text-orange-300' :
                        displayCategory === 'gas' ? 'bg-yellow-500/20 text-yellow-300' :
                        displayCategory === 'studio' ? 'bg-pink-500/20 text-pink-300' :
                        displayCategory === 'rent' ? 'bg-blue-500/20 text-blue-300' :
                        displayCategory === 'income' ? 'bg-green-500/20 text-green-300' :
                        'bg-purple-500/20 text-purple-300'
                      }`}>
                        {displayCategory}
                      </span>
                    </td>
                    <td className="py-3 px-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs whitespace-nowrap ${
                        isIncome 
                          ? 'bg-green-500/20 text-green-300' 
                          : 'bg-red-500/20 text-red-300'
                      }`} data-testid={`type-badge-${globalIdx}`}>
                        <span>{isIncome ? '💰' : '💸'}</span>
                        <span>{isIncome ? 'Income' : 'Expense'}</span>
                      </span>
                    </td>
                    <td className="py-3 px-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs whitespace-nowrap ${
                        displayTag === 'business' 
                          ? 'bg-amber-500/20 text-amber-300' 
                          : displayTag === 'cash'
                          ? 'bg-green-500/20 text-green-300'
                          : 'bg-sky-500/20 text-sky-300'
                      }`}>
                        <span>{displayTag === 'business' ? '💼' : displayTag === 'cash' ? '💵' : '👤'}</span>
                        <span>{displayTag === 'business' ? 'Business' : displayTag === 'cash' ? 'Cash' : 'Personal'}</span>
                      </span>
                    </td>
                    <td className="py-3 px-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs whitespace-nowrap ${
                        txn.source === 'uploaded' 
                          ? 'bg-emerald-500/20 text-emerald-300' 
                          : 'bg-cyan-500/20 text-cyan-300'
                      }`}>
                        {txn.source === 'uploaded' ? 'Uploaded' : 'Bank'}
                      </span>
                    </td>
                    <td className={`py-3 px-2 text-right font-medium ${isIncome ? 'text-green-400' : 'text-red-400'}`}>
                      {isIncome ? '+' : '-'}${displayAmount.toFixed(2)}
                    </td>
                    <td className="py-3 px-2 text-center" onClick={(e) => e.stopPropagation()}>
                      {txn.receipt_url ? (
                        <button
                          onClick={() => setReceiptPreview({ url: txn.receipt_url, name: displayName })}
                          className="p-1.5 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors"
                          title="View Receipt"
                          data-testid={`view-receipt-${globalIdx}`}
                        >
                          <Receipt size={14} />
                        </button>
                      ) : isEditable ? (
                        <label className="p-1.5 rounded-lg bg-gray-700/50 text-gray-400 hover:bg-gray-700 hover:text-white transition-colors cursor-pointer inline-flex">
                          <Upload size={14} />
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            className="hidden"
                            onChange={(e) => handleReceiptUpload(txnId, e.target.files?.[0])}
                            data-testid={`upload-receipt-${globalIdx}`}
                          />
                        </label>
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
              <div className="text-gray-400 text-sm">
                Page {currentPage} of {totalPages} ({filteredTransactions.length} total transactions)
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium disabled:text-gray-600 disabled:cursor-not-allowed text-gray-400 hover:text-white hover:bg-gray-800"
                  data-testid="pagination-first"
                >
                  First
                </button>
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium disabled:text-gray-600 disabled:cursor-not-allowed text-gray-400 hover:text-white hover:bg-gray-800"
                  data-testid="pagination-prev"
                >
                  ← Prev
                </button>
                
                {getPageNumbers().map((page, idx) => (
                  <button
                    key={idx}
                    onClick={() => typeof page === 'number' && goToPage(page)}
                    disabled={page === '...'}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      page === currentPage
                        ? 'bg-purple-600 text-white'
                        : page === '...'
                        ? 'text-gray-500 cursor-default'
                        : 'text-gray-400 hover:text-white hover:bg-gray-800'
                    }`}
                    data-testid={`pagination-page-${page}`}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium disabled:text-gray-600 disabled:cursor-not-allowed text-gray-400 hover:text-white hover:bg-gray-800"
                  data-testid="pagination-next"
                >
                  Next →
                </button>
                <button
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium disabled:text-gray-600 disabled:cursor-not-allowed text-gray-400 hover:text-white hover:bg-gray-800"
                  data-testid="pagination-last"
                >
                  Last
                </button>
                
                {/* Go to page input */}
                <div className="flex items-center gap-2 ml-4 pl-4 border-l border-gray-700">
                  <span className="text-gray-400 text-sm">Go to:</span>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    placeholder={currentPage}
                    className="w-16 bg-gray-800 border border-gray-600 rounded-lg px-2 py-1 text-white text-sm text-center"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const page = parseInt(e.target.value);
                        if (page >= 1 && page <= totalPages) {
                          goToPage(page);
                          e.target.value = '';
                        }
                      }
                    }}
                    data-testid="pagination-goto"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      ) : !loading && (
        <div className="text-center py-8">
          <Receipt size={48} className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">No transactions found</p>
          <p className="text-gray-500 text-sm">Upload documents or sync your bank account to see transactions</p>
        </div>
      )}

      {/* Add Transaction Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Add Transaction</h3>
              <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-gray-800 rounded-lg">
                <X size={18} className="text-gray-400" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Date */}
              <div>
                <label className="text-gray-400 text-sm block mb-1.5">Date</label>
                <input
                  type="date"
                  value={newTransaction.date}
                  onChange={(e) => setNewTransaction(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full bg-black border border-gray-600 rounded-xl px-4 py-2.5 text-white"
                  data-testid="add-txn-date"
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-gray-400 text-sm block mb-1.5">Description</label>
                <input
                  type="text"
                  value={newTransaction.description}
                  onChange={(e) => setNewTransaction(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="e.g., Grocery shopping at Whole Foods"
                  className="w-full bg-black border border-gray-600 rounded-xl px-4 py-2.5 text-white placeholder-gray-500"
                  data-testid="add-txn-description"
                />
              </div>

              {/* Amount */}
              <div>
                <label className="text-gray-400 text-sm block mb-1.5">Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={newTransaction.amount}
                  onChange={(e) => setNewTransaction(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0.00"
                  className="w-full bg-black border border-gray-600 rounded-xl px-4 py-2.5 text-white placeholder-gray-500"
                  data-testid="add-txn-amount"
                />
              </div>

              {/* Type (Income/Expense) */}
              <div>
                <label className="text-gray-400 text-sm block mb-1.5">Type</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setNewTransaction(prev => ({ ...prev, type: 'expense' }))}
                    className={`flex-1 py-2.5 rounded-xl font-medium transition-colors ${
                      newTransaction.type === 'expense'
                        ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                        : 'bg-gray-800 text-gray-400 border border-gray-700'
                    }`}
                    data-testid="add-txn-type-expense"
                  >
                    Expense
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewTransaction(prev => ({ ...prev, type: 'income' }))}
                    className={`flex-1 py-2.5 rounded-xl font-medium transition-colors ${
                      newTransaction.type === 'income'
                        ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                        : 'bg-gray-800 text-gray-400 border border-gray-700'
                    }`}
                    data-testid="add-txn-type-income"
                  >
                    Income
                  </button>
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="text-gray-400 text-sm block mb-1.5">Category</label>
                <select
                  value={newTransaction.category}
                  onChange={(e) => setNewTransaction(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full bg-black border border-gray-600 rounded-xl px-4 py-2.5 text-white"
                  data-testid="add-txn-category"
                >
                  {categoryTabs.filter(c => c.id !== 'all').map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Tag (Business/Personal/Cash) */}
              <div>
                <label className="text-gray-400 text-sm block mb-1.5">Tag</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setNewTransaction(prev => ({ ...prev, tag: 'personal' }))}
                    className={`flex-1 py-2.5 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
                      newTransaction.tag === 'personal'
                        ? 'bg-sky-500/20 text-sky-400 border border-sky-500/50'
                        : 'bg-gray-800 text-gray-400 border border-gray-700'
                    }`}
                    data-testid="add-txn-tag-personal"
                  >
                    👤 Personal
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewTransaction(prev => ({ ...prev, tag: 'business' }))}
                    className={`flex-1 py-2.5 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
                      newTransaction.tag === 'business'
                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/50'
                        : 'bg-gray-800 text-gray-400 border border-gray-700'
                    }`}
                    data-testid="add-txn-tag-business"
                  >
                    💼 Business
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewTransaction(prev => ({ ...prev, tag: 'cash' }))}
                    className={`flex-1 py-2.5 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
                      newTransaction.tag === 'cash'
                        ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                        : 'bg-gray-800 text-gray-400 border border-gray-700'
                    }`}
                    data-testid="add-txn-tag-cash"
                  >
                    💵 Cash
                  </button>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTransaction}
                disabled={saving || !newTransaction.description.trim() || !newTransaction.amount}
                className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="save-transaction-btn"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                {saving ? 'Saving...' : 'Add Transaction'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Transaction Modal */}
      {showEditModal && editingTransaction && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowEditModal(false)}>
          <div className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Edit Transaction</h3>
              <button onClick={() => setShowEditModal(false)} className="p-2 hover:bg-gray-800 rounded-lg">
                <X size={18} className="text-gray-400" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Date */}
              <div>
                <label className="text-gray-400 text-sm block mb-1.5">Date</label>
                <input
                  type="date"
                  value={editForm.date}
                  onChange={(e) => setEditForm(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full bg-black border border-gray-600 rounded-xl px-4 py-2.5 text-white"
                  data-testid="edit-txn-date"
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-gray-400 text-sm block mb-1.5">Description</label>
                <input
                  type="text"
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="e.g., Grocery shopping at Whole Foods"
                  className="w-full bg-black border border-gray-600 rounded-xl px-4 py-2.5 text-white placeholder-gray-500"
                  data-testid="edit-txn-description"
                />
              </div>

              {/* Amount */}
              <div>
                <label className="text-gray-400 text-sm block mb-1.5">Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.amount}
                  onChange={(e) => setEditForm(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0.00"
                  className="w-full bg-black border border-gray-600 rounded-xl px-4 py-2.5 text-white placeholder-gray-500"
                  data-testid="edit-txn-amount"
                />
              </div>

              {/* Type (Income/Expense) */}
              <div>
                <label className="text-gray-400 text-sm block mb-1.5">Type</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditForm(prev => ({ ...prev, type: 'expense' }))}
                    className={`flex-1 py-2.5 rounded-xl font-medium transition-colors ${
                      editForm.type === 'expense'
                        ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                        : 'bg-gray-800 text-gray-400 border border-gray-700'
                    }`}
                    data-testid="edit-txn-type-expense"
                  >
                    Expense
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditForm(prev => ({ ...prev, type: 'income' }))}
                    className={`flex-1 py-2.5 rounded-xl font-medium transition-colors ${
                      editForm.type === 'income'
                        ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                        : 'bg-gray-800 text-gray-400 border border-gray-700'
                    }`}
                    data-testid="edit-txn-type-income"
                  >
                    Income
                  </button>
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="text-gray-400 text-sm block mb-1.5">Category</label>
                <select
                  value={editForm.category}
                  onChange={(e) => setEditForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full bg-black border border-gray-600 rounded-xl px-4 py-2.5 text-white"
                  data-testid="edit-txn-category"
                >
                  {categoryTabs.filter(c => c.id !== 'all').map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Tag (Business/Personal/Cash) */}
              <div>
                <label className="text-gray-400 text-sm block mb-1.5">Tag</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditForm(prev => ({ ...prev, tag: 'personal' }))}
                    className={`flex-1 py-2.5 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
                      editForm.tag === 'personal'
                        ? 'bg-sky-500/20 text-sky-400 border border-sky-500/50'
                        : 'bg-gray-800 text-gray-400 border border-gray-700'
                    }`}
                    data-testid="edit-txn-tag-personal"
                  >
                    👤 Personal
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditForm(prev => ({ ...prev, tag: 'business' }))}
                    className={`flex-1 py-2.5 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
                      editForm.tag === 'business'
                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/50'
                        : 'bg-gray-800 text-gray-400 border border-gray-700'
                    }`}
                    data-testid="edit-txn-tag-business"
                  >
                    💼 Business
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditForm(prev => ({ ...prev, tag: 'cash' }))}
                    className={`flex-1 py-2.5 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
                      editForm.tag === 'cash'
                        ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                        : 'bg-gray-800 text-gray-400 border border-gray-700'
                    }`}
                    data-testid="edit-txn-tag-cash"
                  >
                    💵 Cash
                  </button>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-700 flex gap-3">
              <button
                onClick={handleDeleteTransaction}
                disabled={saving}
                className="px-4 py-2.5 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="delete-transaction-btn"
              >
                <Trash2 size={16} />
                Delete
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving || !editForm.description.trim() || !editForm.amount}
                className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="update-transaction-btn"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                {saving ? 'Saving...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Management Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowCategoryModal(false)}>
          <div className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">
                {editingCategory ? (editingCategory.isDefault ? 'Edit Keywords' : 'Edit Category') : 'Add New Category'}
              </h3>
              <button onClick={() => setShowCategoryModal(false)} className="p-2 hover:bg-gray-800 rounded-lg">
                <X size={18} className="text-gray-400" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Name (disabled for default categories) */}
              <div>
                <label className="text-gray-400 text-sm block mb-1.5">Category Name</label>
                <input
                  type="text"
                  value={newCategoryForm.name}
                  onChange={(e) => setNewCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Medical, Taxes, Marketing"
                  disabled={editingCategory?.isDefault}
                  className="w-full bg-black border border-gray-600 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 disabled:opacity-50"
                  data-testid="category-name-input"
                />
              </div>

              {/* Icon Selector (disabled for default categories) */}
              <div>
                <label className="text-gray-400 text-sm block mb-1.5">Icon</label>
                <div className="flex flex-wrap gap-2">
                  {['📁', '💼', '🏥', '📝', '💳', '🎯', '📊', '🔧', '💻', '🎨', '📦', '🚗', '🏠', '📱', '🍔', '✈️', '🎬', '💡', '🛒', '💰'].map(icon => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => !editingCategory?.isDefault && setNewCategoryForm(prev => ({ ...prev, icon }))}
                      disabled={editingCategory?.isDefault}
                      className={`w-10 h-10 rounded-lg text-xl flex items-center justify-center transition-colors ${
                        newCategoryForm.icon === icon
                          ? 'bg-emerald-500 ring-2 ring-emerald-400'
                          : 'bg-gray-800 hover:bg-gray-700'
                      } ${editingCategory?.isDefault ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              {/* Keywords */}
              <div>
                <label className="text-gray-400 text-sm block mb-1.5">
                  Keywords <span className="text-gray-500">(comma-separated, used for auto-categorization)</span>
                </label>
                <textarea
                  value={newCategoryForm.keywords}
                  onChange={(e) => setNewCategoryForm(prev => ({ ...prev, keywords: e.target.value }))}
                  placeholder="e.g., doctor, hospital, pharmacy, medical"
                  rows={3}
                  className="w-full bg-black border border-gray-600 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 resize-none"
                  data-testid="category-keywords-input"
                />
                <p className="text-gray-500 text-xs mt-1">
                  When you click "Scan", transactions containing these keywords will be assigned to this category.
                </p>
              </div>
            </div>

            <div className="p-4 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => setShowCategoryModal(false)}
                className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCategory}
                disabled={!editingCategory?.isDefault && !newCategoryForm.name.trim()}
                className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="save-category-btn"
              >
                <Check size={16} />
                {editingCategory ? 'Save Changes' : 'Add Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Preview Modal */}
      {receiptPreview && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setReceiptPreview(null)}
        >
          <div 
            className="relative max-w-4xl max-h-[90vh] mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="absolute -top-12 left-0 right-0 flex items-center justify-between">
              <h3 className="text-white font-medium truncate pr-4">
                Receipt: {receiptPreview.name}
              </h3>
              <div className="flex items-center gap-2">
                <a
                  href={receiptPreview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-lg flex items-center gap-1.5"
                >
                  <ExternalLink size={14} />
                  Open
                </a>
                <button
                  onClick={() => setReceiptPreview(null)}
                  className="p-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="bg-gray-900 rounded-xl overflow-hidden border border-gray-700 shadow-2xl">
              {receiptPreview.url.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={receiptPreview.url}
                  className="w-full h-[80vh] min-w-[600px]"
                  title="Receipt PDF"
                />
              ) : (
                <img
                  src={receiptPreview.url}
                  alt="Receipt"
                  className="max-w-full max-h-[80vh] object-contain"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 24 24" fill="none" stroke="%23666" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>';
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const RealEstateManagingPartnerFinances = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = useUserId();
  const { isDemoMode, demoData, triggerViewOnlyWarning } = useDemoMode();
  
  const [selectedTransactionFilter, setSelectedTransactionFilter] = useState('All');
  const [upcomingBills, setUpcomingBills] = useState([]);
  const [loadingBills, setLoadingBills] = useState(true);
  const [lastBillsSync, setLastBillsSync] = useState(null);
  
  // Financial connections state
  const [connections, setConnections] = useState(() => {
    const saved = localStorage.getItem('financeConnections');
    return saved ? JSON.parse(saved) : [];
  });

  // Default transaction filter categories
  const defaultTransactionFilters = [
    { id: 'All', name: 'All', icon: '📊' },
    { id: 'Income', name: 'Income', icon: '💰' },
    { id: 'Equipment', name: 'Equipment', icon: '🔧' },
    { id: 'Software', name: 'Software', icon: '💻' }
  ];

  // Load custom transaction filters from localStorage
  const [customTransactionFilters, setCustomTransactionFilters] = useState(() => {
    try {
      const saved = localStorage.getItem('finance_transaction_filters');
      return saved ? JSON.parse(saved) : defaultTransactionFilters;
    } catch { return defaultTransactionFilters; }
  });

  // Modal states for editing transaction categories
  const [showTransactionFilterModal, setShowTransactionFilterModal] = useState(false);
  const [newFilterName, setNewFilterName] = useState('');
  const [newFilterIcon, setNewFilterIcon] = useState('📋');
  const [editingFilter, setEditingFilter] = useState(null);
  const [editFilterValue, setEditFilterValue] = useState('');

  const filterIcons = ['📋', '💰', '🔧', '💻', '📊', '📈', '💳', '🏦', '💵', '🎨', '📦', '🚗', '🏠', '📱', '🍔', '✈️', '🎬', '💡', '🔌', '📝'];

  // Persist custom filters to localStorage
  useEffect(() => {
    localStorage.setItem('finance_transaction_filters', JSON.stringify(customTransactionFilters));
  }, [customTransactionFilters]);

  // Transaction filter CRUD operations
  const addTransactionFilter = () => {
    if (!newFilterName.trim()) return;
    if (customTransactionFilters.some(f => f.id === newFilterName)) { 
      toast.error('Filter already exists'); 
      return; 
    }
    const newFilter = { id: newFilterName, name: newFilterName, icon: newFilterIcon };
    const updated = [...customTransactionFilters, newFilter];
    setCustomTransactionFilters(updated);
    setNewFilterName('');
    setNewFilterIcon('📋');
    toast.success(`Added "${newFilterName}" filter`);
  };

  const updateTransactionFilter = (oldId, newName) => {
    if (!newName.trim()) return;
    const updated = customTransactionFilters.map(f => f.id === oldId ? { ...f, id: newName, name: newName } : f);
    setCustomTransactionFilters(updated);
    if (selectedTransactionFilter === oldId) setSelectedTransactionFilter(newName);
    setEditingFilter(null);
    setEditFilterValue('');
    toast.success(`Filter renamed to "${newName}"`);
  };

  const deleteTransactionFilter = (filterId) => {
    if (['All', 'Income'].includes(filterId)) { 
      toast.error('Cannot delete default filter'); 
      return; 
    }
    const updated = customTransactionFilters.filter(f => f.id !== filterId);
    setCustomTransactionFilters(updated);
    if (selectedTransactionFilter === filterId) setSelectedTransactionFilter('All');
    toast.success('Filter deleted');
  };

  // API URL - use same origin
  const apiUrl = typeof window !== 'undefined' ? window.location.origin : '';

  // Save connections to localStorage
  useEffect(() => {
    localStorage.setItem('financeConnections', JSON.stringify(connections));
  }, [connections]);

  // Fetch bills from backend on mount
  useEffect(() => {
    fetchBills();
  }, []);

  const fetchBills = async () => {
    setLoadingBills(true);
    try {
      const response = await fetch(`${apiUrl}/api/excel/get-bills`);
      const data = await response.json();
      
      if (data.status === 'success' && data.bills && data.bills.length > 0) {
        setUpcomingBills(data.bills);
        setLastBillsSync(new Date());
        console.log(`✅ Loaded ${data.bills.length} bills from backend`);
      } else {
        // Use default bills if no data from backend
        setUpcomingBills(defaultBills);
        console.log('📋 Using default bills (no synced data found)');
      }
    } catch (error) {
      console.error('Error fetching bills:', error);
      setUpcomingBills(defaultBills);
    } finally {
      setLoadingBills(false);
    }
  };

  // Default bills (shown when no synced data) - empty for new users
  const defaultBills = [];

  // Monthly income data for small sparkline charts - empty for new users
  const monthlyIncomeData = [];

  // Monthly expenses data for small sparkline charts - empty for new users
  const monthlyExpensesData = [];

  // Recent transactions - empty for new users, fetched from API
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loadingRecentTransactions, setLoadingRecentTransactions] = useState(true);
  
  // Sorting state for recent transactions
  const [sortField, setSortField] = useState('date'); // 'date', 'name', 'amount'
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc', 'desc'
  
  // Toggle sort direction or change sort field
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Fetch recent transactions on mount
  useEffect(() => {
    const fetchRecentTransactions = async () => {
      // Use demo data if in demo mode
      if (isDemoMode && demoData?.finances?.transactions) {
        const demoTransactions = demoData.finances.transactions.map(t => ({
          id: t.id,
          date: t.date,
          name: t.description,
          description: t.description,
          amount: t.amount,
          type: t.type || 'expense',
          category: t.category || 'General',
          client: t.category || 'Transaction'
        }));
        setRecentTransactions(demoTransactions);
        setLoadingRecentTransactions(false);
        console.log(`💰 Demo mode: Loaded ${demoTransactions.length} demo transactions`);
        return;
      }
      
      if (!userId) return;
      
      setLoadingRecentTransactions(true);
      try {
        const response = await fetch(`${apiUrl}/api/finances/transactions?user_id=${userId}`);
        const data = await response.json();
        
        if (data.transactions && data.transactions.length > 0) {
          // Sort by date descending and take most recent 10
          const sorted = data.transactions.sort((a, b) => new Date(b.date) - new Date(a.date));
          // Add client field for display compatibility
          const withClient = sorted.map(t => ({
            ...t,
            name: t.description || t.name,
            client: t.category || 'Transaction'
          }));
          setRecentTransactions(withClient.slice(0, 10));
        }
      } catch (error) {
        console.error('Error fetching recent transactions:', error);
      } finally {
        setLoadingRecentTransactions(false);
      }
    };

    fetchRecentTransactions();
  }, [apiUrl, userId, isDemoMode, demoData]);

  const transactionFilters = customTransactionFilters.map(f => f.id);

  // Fixed filter logic: "Income" filters by type, others filter by category (case-insensitive)
  // Then sort by the selected field and direction
  const filteredTransactions = (() => {
    let filtered = selectedTransactionFilter === 'All' 
      ? recentTransactions 
      : selectedTransactionFilter === 'Income'
        ? recentTransactions.filter(t => t.type === 'income')
        : recentTransactions.filter(t => 
            (t.category || '').toLowerCase() === selectedTransactionFilter.toLowerCase()
          );
    
    // Apply sorting
    return [...filtered].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'date':
          aVal = new Date(a.date || '1970-01-01');
          bVal = new Date(b.date || '1970-01-01');
          break;
        case 'name':
          aVal = (a.name || a.description || '').toLowerCase();
          bVal = (b.name || b.description || '').toLowerCase();
          break;
        case 'amount':
          aVal = Math.abs(a.amount || 0);
          bVal = Math.abs(b.amount || 0);
          break;
        default:
          aVal = new Date(a.date || '1970-01-01');
          bVal = new Date(b.date || '1970-01-01');
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
  })();

  // Format date helper
  const formatDueDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-black text-white p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold mb-1">Finances</h1>
        <p className="text-gray-400 text-sm">Manage your financial overview</p>
      </div>

      {/* Connections Panel */}
      <ConnectionsPanel
        connections={connections}
        setConnections={setConnections}
        userId={userId}
        onAccountsUpdate={(accounts) => {
          // Update balance displays when accounts are synced
          if (accounts && accounts.length > 0) {
            const totalBalance = accounts.reduce((sum, acc) => sum + (acc.balances?.current || 0), 0);
            // Store in localStorage for persistence
            localStorage.setItem('plaidAccountData', JSON.stringify({ accounts, totalBalance }));
          }
        }}
      />

      {/* Top Row - Balance, Income, Expenses */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* My Balance Card - Purple/Pink Gradient */}
        <div className="bg-gradient-to-br from-purple-600 via-pink-500 to-pink-400 rounded-2xl p-5 relative overflow-hidden">
          <div className="flex items-center justify-between mb-1">
            <span className="text-white/80 text-sm">My Balance</span>
            <button className="flex items-center gap-1 bg-white/20 hover:bg-white/30 rounded-lg px-2 py-1 text-xs transition-colors">
              USD <ChevronDown size={14} />
            </button>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-1">
            {connections.length > 0 
              ? `$${connections.reduce((sum, c) => sum + (c.totalBalance || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
              : '$0.00'
            }
          </h2>
          <p className="text-white/70 text-sm mb-4">
            {connections.length > 0 ? 'Available for use' : 'Connect accounts to see balance'}
          </p>
          
          <div className="flex gap-3">
            <button className="flex-1 bg-white hover:bg-gray-100 text-gray-800 font-semibold py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors">
              <Send size={16} />
              Send
            </button>
            <button className="flex-1 bg-purple-700/50 hover:bg-purple-700/70 text-white font-semibold py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 border border-white/20 transition-colors">
              <Download size={16} />
              Request
            </button>
          </div>
        </div>

        {/* Monthly Income Card */}
        <div className="bg-black border border-gray-700 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Monthly Income</span>
            {monthlyIncomeData.length > 0 && (
              <span className="text-green-400 text-sm font-semibold">↗ +12.5%</span>
            )}
          </div>
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            {monthlyIncomeData.length > 0 
              ? `$${monthlyIncomeData[monthlyIncomeData.length - 1]?.value?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}`
              : '$0.00'
            }
          </h2>
          
          {/* Mini Line Chart */}
          <div className="h-16">
            {monthlyIncomeData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyIncomeData}>
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#A855F7" 
                    strokeWidth={2}
                    dot={false}
                  />
                  <XAxis 
                    dataKey="month" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: '#6B7280' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-600 text-sm">
                No income data yet
              </div>
            )}
          </div>
        </div>

        {/* Monthly Expenses Card */}
        <div className="bg-black border border-gray-700 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Monthly Expenses</span>
            {monthlyExpensesData.length > 0 && (
              <span className="text-red-400 text-sm font-semibold">↘ +8.2%</span>
            )}
          </div>
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            {monthlyExpensesData.length > 0 
              ? `$${monthlyExpensesData[monthlyExpensesData.length - 1]?.value?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}`
              : '$0.00'
            }
          </h2>
          
          {/* Mini Line Chart */}
          <div className="h-16">
            {monthlyExpensesData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyExpensesData}>
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#A855F7" 
                    strokeWidth={2}
                    dot={false}
                  />
                  <XAxis 
                    dataKey="month" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: '#6B7280' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-600 text-sm">
                No expense data yet
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Row - Recent Transactions & Upcoming Bills */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Recent Transactions Section */}
        <div className="bg-black border border-gray-700 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Recent Transactions</h3>
            <button 
              onClick={() => navigate('/all-transactions')}
              className="text-orange-400 hover:text-orange-300 text-sm font-semibold transition-colors"
            >
              View all
            </button>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2 mb-4 flex-wrap items-center">
            {customTransactionFilters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setSelectedTransactionFilter(filter.id)}
                data-testid={`filter-${filter.id.toLowerCase()}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  selectedTransactionFilter === filter.id
                    ? 'bg-cyan-500 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                <span>{filter.icon}</span>
                {filter.name}
              </button>
            ))}
            {/* Edit Categories Button */}
            <button
              onClick={() => setShowTransactionFilterModal(true)}
              data-testid="edit-transaction-categories-btn"
              className="px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap bg-transparent border border-dashed border-gray-500 text-gray-400 hover:bg-gray-800 hover:text-white flex items-center gap-1"
              title="Edit Categories"
            >
              <Pencil size={14} />
              Edit
            </button>
          </div>

          {/* Sortable Column Headers */}
          <div className="flex items-center justify-between px-2 py-2 mb-2 border-b border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-10"></div> {/* Spacer for icon */}
              <button 
                onClick={() => handleSort('name')}
                className={`text-xs font-semibold uppercase tracking-wider flex items-center gap-1 transition-colors ${
                  sortField === 'name' ? 'text-cyan-400' : 'text-gray-500 hover:text-gray-300'
                }`}
                data-testid="sort-name"
              >
                Name
                {sortField === 'name' && (
                  <span className="text-[10px]">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </button>
            </div>
            <div className="flex items-center gap-6">
              <button 
                onClick={() => handleSort('date')}
                className={`text-xs font-semibold uppercase tracking-wider flex items-center gap-1 transition-colors ${
                  sortField === 'date' ? 'text-cyan-400' : 'text-gray-500 hover:text-gray-300'
                }`}
                data-testid="sort-date"
              >
                Date
                {sortField === 'date' && (
                  <span className="text-[10px]">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </button>
              <button 
                onClick={() => handleSort('amount')}
                className={`text-xs font-semibold uppercase tracking-wider flex items-center gap-1 transition-colors min-w-[70px] text-right justify-end ${
                  sortField === 'amount' ? 'text-cyan-400' : 'text-gray-500 hover:text-gray-300'
                }`}
                data-testid="sort-amount"
              >
                Amount
                {sortField === 'amount' && (
                  <span className="text-[10px]">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </button>
            </div>
          </div>

          {/* Transactions List */}
          <div className="space-y-3" data-testid="recent-transactions-list">
            {loadingRecentTransactions ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={24} className="animate-spin text-gray-500" />
                <span className="ml-2 text-gray-500">Loading transactions...</span>
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No transactions found</p>
                <p className="text-xs mt-1">
                  {selectedTransactionFilter !== 'All' 
                    ? `No ${selectedTransactionFilter} transactions to display` 
                    : 'Connect your accounts to see transactions'}
                </p>
              </div>
            ) : (
              filteredTransactions.map((transaction) => (
                <div 
                  key={transaction.id} 
                  className="flex items-center justify-between py-3 border-b border-gray-800 last:border-0"
                  data-testid={`transaction-${transaction.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      transaction.type === 'income' 
                        ? 'bg-orange-500/20' 
                        : 'bg-gray-700'
                    }`}>
                      {transaction.type === 'income' ? (
                        <svg className="w-5 h-5 text-orange-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 2a8 8 0 100 16 8 8 0 000-16zM8 12a1 1 0 01-1-1V9a1 1 0 012 0v2a1 1 0 01-1 1zm4 0a1 1 0 01-1-1V9a1 1 0 112 0v2a1 1 0 01-1 1z"/>
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V4H6z" clipRule="evenodd"/>
                        </svg>
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{transaction.name}</p>
                      <p className="text-gray-500 text-xs">{transaction.date} • {transaction.client}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-semibold ${
                      transaction.type === 'income' ? 'text-green-400' : 'text-white'
                    }`}>
                      {transaction.type === 'income' ? '+' : '-'}${transaction.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-gray-500 text-xs">Completed</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Upcoming Bills Section */}
        <div className="bg-black border border-gray-700 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Upcoming Bills</h3>
            <div className="flex items-center gap-2">
              <button 
                onClick={fetchBills}
                className="text-gray-400 hover:text-white transition-colors p-1"
                title="Refresh bills"
              >
                <RefreshCw size={16} className={loadingBills ? 'animate-spin' : ''} />
              </button>
              <button 
                onClick={() => navigate('/all-bills')}
                className="text-orange-400 hover:text-orange-300 text-sm font-semibold transition-colors"
              >
                View all
              </button>
            </div>
          </div>

          {/* Bills List */}
          <div className="space-y-3">
            {loadingBills ? (
              <div className="text-center py-8 text-gray-500">
                <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                <p>Loading bills...</p>
              </div>
            ) : upcomingBills.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No bills found</p>
                <p className="text-xs mt-1">Run the sync script to import bills</p>
              </div>
            ) : (
              upcomingBills.map((bill) => (
                <div 
                  key={bill.id} 
                  className="flex items-center justify-between py-3 border-b border-gray-800 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center text-lg">
                      {bill.icon || '📄'}
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{bill.name}</p>
                      <div className="flex items-center gap-1 text-gray-500 text-xs">
                        <Calendar size={12} />
                        <span>Due {formatDueDate(bill.due_date || bill.dueDate)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-semibold">
                      ${(bill.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                    <p className={`text-xs ${bill.status === 'paid' ? 'text-green-400' : bill.status === 'overdue' ? 'text-red-400' : 'text-yellow-400'}`}>
                      {bill.status === 'paid' ? 'Paid' : bill.status === 'overdue' ? 'Overdue' : 'Pending'}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Total Upcoming */}
          {upcomingBills.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-sm">Total Upcoming</span>
                <span className="text-white font-bold text-lg">
                  ${upcomingBills.reduce((sum, bill) => sum + (bill.amount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Document Upload Panel for AI Parsing */}
      <DocumentUploadPanel 
        userId={userId} 
        onTransactionsParsed={(transactions) => {
          // Refresh transaction data if needed
          toast.info('Transactions saved! They will appear in your transaction history.');
        }}
      />

      {/* Transactions Panel for Tax Purposes - Bank Transaction History */}
      <TransactionsPanel userId={userId} />

      {/* Transaction Filter Categories Modal */}
      {showTransactionFilterModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowTransactionFilterModal(false)}>
          <div className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Edit Transaction Categories</h3>
              <button onClick={() => setShowTransactionFilterModal(false)} className="p-2 hover:bg-gray-800 rounded-lg"><X size={18} className="text-gray-400" /></button>
            </div>
            <div className="p-4 border-b border-gray-700">
              <div className="flex gap-2 mb-3">
                <div className="relative">
                  <button className="w-10 h-10 bg-black border border-gray-600 rounded-lg text-xl flex items-center justify-center" onClick={(e) => e.currentTarget.nextElementSibling.classList.toggle('hidden')}>{newFilterIcon}</button>
                  <div className="hidden absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg p-2 z-10 grid grid-cols-5 gap-1 w-40">
                    {filterIcons.map(icon => (<button key={icon} onClick={(e) => { setNewFilterIcon(icon); e.currentTarget.parentElement.classList.add('hidden'); }} className="w-7 h-7 hover:bg-gray-700 rounded text-lg flex items-center justify-center">{icon}</button>))}
                  </div>
                </div>
                <input type="text" value={newFilterName} onChange={(e) => setNewFilterName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addTransactionFilter()} placeholder="New category name..." className="flex-1 px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm" data-testid="new-transaction-filter-input" />
              </div>
              <button onClick={addTransactionFilter} disabled={!newFilterName.trim()} className="w-full px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5" data-testid="add-transaction-filter-btn"><Plus size={14} />Add Category</button>
            </div>
            <div className="p-4 max-h-80 overflow-y-auto space-y-2">
              {customTransactionFilters.map(cat => (
                <div key={cat.id} className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg group">
                  {editingFilter === cat.id ? (
                    <>
                      <span className="text-lg">{cat.icon}</span>
                      <input type="text" value={editFilterValue} onChange={(e) => setEditFilterValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') updateTransactionFilter(cat.id, editFilterValue); if (e.key === 'Escape') { setEditingFilter(null); setEditFilterValue(''); }}} className="flex-1 px-2 py-1 bg-black border border-gray-600 rounded text-white text-sm" autoFocus />
                      <button onClick={() => updateTransactionFilter(cat.id, editFilterValue)} className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"><Check size={14} /></button>
                      <button onClick={() => { setEditingFilter(null); setEditFilterValue(''); }} className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"><X size={14} /></button>
                    </>
                  ) : (
                    <>
                      <span className="w-8 h-8 rounded-lg bg-gray-700 flex items-center justify-center text-sm">{cat.icon}</span>
                      <span className="flex-1 text-white text-sm">{cat.name}{['All', 'Income'].includes(cat.id) && <span className="ml-2 text-xs text-gray-500">(default)</span>}</span>
                      <button onClick={() => { setEditingFilter(cat.id); setEditFilterValue(cat.name); }} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100"><Pencil size={14} /></button>
                      {!['All', 'Income'].includes(cat.id) && <button onClick={() => deleteTransactionFilter(cat.id)} className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>}
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-gray-700">
              <button onClick={() => setShowTransactionFilterModal(false)} className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium" data-testid="done-transaction-filter-btn">Done</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default RealEstateManagingPartnerFinances;
